import { clerkMiddleware } from "@clerk/tanstack-react-start/server";
import {
  createCsrfMiddleware,
  createMiddleware,
  createServerOnlyFn,
  createStart,
} from "@tanstack/react-start";

import {
  sentryFunctionMiddleware,
  sentryRequestMiddleware,
} from "~/lib/sentry-middleware";
import { serverFnProtocolMiddleware } from "~/lib/server-fn-protocol-middleware";
import { userFacingErrorAdapter } from "~/lib/shared/user-facing-error-adapter";

// `.server()` callbacks are NOT compiled out of the client bundle, so a plain
// dynamic import here would still emit db-middleware (and the Hyperdrive/env
// modules behind it) as client chunks. createServerOnlyFn severs that import
// edge in the client build.
const importDbMiddleware = createServerOnlyFn(
  () => import("~/lib/db-middleware"),
);

const dbMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const { configureDb } = await importDbMiddleware();
    const db = await configureDb();
    return next({
      context: { db },
    });
  },
);

// Rejects server function requests that another site sent (403). Start adds
// this check by itself only when there is no `createStart`, so it must be
// listed here. The filter is required: page loads and API webhooks do not
// carry same-origin headers and must not be checked.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

// Sentry middleware is listed first so it wraps every other middleware in the
// chain — any error thrown deeper (Clerk auth, db setup, server fn handler,
// API route handler, SSR render) bubbles out through this layer and gets
// captured before TanStack converts it into an error response. The protocol
// guard runs after the CSRF check and before Clerk, which crashes on server
// function requests whose handler produces no response (see
// server-fn-protocol-middleware.ts).
export const startInstance = createStart(() => {
  return {
    requestMiddleware: [
      sentryRequestMiddleware,
      csrfMiddleware,
      serverFnProtocolMiddleware,
      clerkMiddleware(),
    ],
    functionMiddleware: [sentryFunctionMiddleware, dbMiddleware],
    // Without it a server function's thrown `UserFacingError` reaches the
    // browser as a plain `Error`, and no component can show its message.
    serializationAdapters: [userFacingErrorAdapter],
  };
});
